import { useCallback, useEffect, useRef, useState, Suspense } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Header } from '@/components/common/Header'
import { Footer } from '@/components/common/Footer'
import { PageLoader } from '@/components/common/PageLoader'
import { ScrollToTopButton } from '@/components/common/ScrollToTopButton'

const LOADER_NAVIGATE_AT = 300 // ms — let the loader fully fade in before swapping pages
const LOADER_TOTAL_MS = 2500 // ms — total time the loader stays on screen

function ScrollToTop() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (hash) {
      // Small delay lets the new page render before we scroll. Instant (not
      // smooth) so the jump never plays out visibly — the loader masks it.
      const id = setTimeout(() => {
        const el = document.querySelector(hash)
        if (el) {
          el.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' })
        }
      }, 80)
      return () => clearTimeout(id)
    }
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior })
  }, [pathname, hash])
  return null
}

// Intercepts clicks on any internal link anywhere in the app (header, footer,
// mega menu, in-page CTAs, ...) so a transition can play before the route
// actually changes, instead of refactoring every <Link> individually.
function useInternalLinkInterceptor(onNavigate: (to: string) => void) {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

      const anchor = (e.target as HTMLElement | null)?.closest?.('a')
      if (!anchor) return
      if (anchor.target && anchor.target !== '_self') return
      if (anchor.hasAttribute('download')) return

      let url: URL
      try {
        url = new URL(anchor.href, window.location.href)
      } catch {
        return
      }
      if (url.origin !== window.location.origin) return

      const current = window.location.pathname + window.location.search + window.location.hash
      const target = url.pathname + url.search + url.hash
      if (target === current) return

      e.preventDefault()
      onNavigate(target)
    }

    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [onNavigate])
}

export function RootLayout() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const pendingTo = useRef<string | null>(null)

  const startTransition = useCallback(
    (to: string) => {
      if (loading) return
      pendingTo.current = to
      setLoading(true)
    },
    [loading],
  )

  useInternalLinkInterceptor(startTransition)

  // Scroll lock — kept in its own effect, keyed only on `loading`, and always
  // forces the styles back to '' on the way out instead of trusting a
  // captured "previous value" (which could itself have been 'hidden' left
  // over from something else, wedging scroll permanently).
  useEffect(() => {
    const html = document.documentElement
    const body = document.body

    if (!loading) {
      html.style.overflow = ''
      body.style.overflow = ''
      return
    }

    html.style.overflow = 'hidden'
    body.style.overflow = 'hidden'

    // overflow: hidden alone doesn't reliably block touch/wheel scrolling in
    // every mobile browser, so also swallow the gestures directly.
    const preventScroll = (e: Event) => e.preventDefault()
    document.addEventListener('touchmove', preventScroll, { passive: false })
    document.addEventListener('wheel', preventScroll, { passive: false })

    return () => {
      html.style.overflow = ''
      body.style.overflow = ''
      document.removeEventListener('touchmove', preventScroll)
      document.removeEventListener('wheel', preventScroll)
    }
  }, [loading])

  useEffect(() => {
    if (!loading) return

    const navigateTimer = setTimeout(() => {
      if (pendingTo.current) void navigate(pendingTo.current)
    }, LOADER_NAVIGATE_AT)

    const hideTimer = setTimeout(() => {
      setLoading(false)
      pendingTo.current = null
    }, LOADER_TOTAL_MS)

    return () => {
      clearTimeout(navigateTimer)
      clearTimeout(hideTimer)
    }
  }, [loading, navigate])

  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <Suspense fallback={<PageLoader show={true} />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
      <ScrollToTopButton />
      <PageLoader show={loading} />
    </div>
  )
}
